"use client";
import { useState, useCallback, useEffect } from "react";
import RouterLink from "@/components/ui/router-link";
import { Button } from "@/components/ui/button";
import { DirectusImage } from "@/components/DirectusImage";
import {
  Carousel as CarouselContainer,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { cn } from "@/lib/utils";

export default function Carousel(props: {
  data?: {
    title?: string;
    description?: string;
    button?: {
      text: string;
      href?: string;
    };
    video?: string;
    image?: string;
    color?: string;
  }[];
}) {
  const [current, setCurrent] = useState(0);
  const [mainApi, setMainApi] = useState<CarouselApi | null>(null);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onSelect = useCallback(() => {
    if (!mainApi) {
      return;
    }
    setCurrent(mainApi.selectedScrollSnap() || 0);
  }, [mainApi]);

  useEffect(() => {
    if (!mainApi) {
      return;
    }
    setScrollSnaps(mainApi.scrollSnapList());

    mainApi.on("reInit", () => {
      setScrollSnaps(mainApi.scrollSnapList());
    });
  }, [mainApi]);

  useEffect(() => {
    if (!mainApi) {
      return;
    }
    onSelect();
    mainApi.on("select", onSelect).on("reInit", onSelect);
  }, [mainApi, onSelect]);
  if (!props.data?.length) {
    return null;
  }
  return (
    <div className="w-full max-lg:px-6 max-lg:py-5">
      <CarouselContainer
        className="relative w-full"
        opts={{ loop: true }}
        setApi={setMainApi}
        plugins={[
          Autoplay({
            playOnInit: true,
            delay: 5000,
            stopOnInteraction: false,
            stopOnMouseEnter: true,
          }),
        ]}>
        <CarouselContent className="m-0">
          {props.data?.map((el, index) => (
            <CarouselItem
              className="relative h-[45vw] overflow-hidden p-0 max-md:rounded-lg lg:h-[650px]"
              key={index}>
              <div
                className="relative z-10 mx-auto h-full w-full pt-8 max-xl:px-10 max-lg:px-6 md:pt-[100px] lg:pt-[170px] xl:w-[1200px]"
                style={{
                  color: el.color,
                }}>
                <p className="line-clamp-3 whitespace-pre-line text-lg font-bold leading-normal en:text-base md:text-3xl md:leading-normal lg:min-h-[140px] lg:text-5xl lg:leading-normal en:lg:text-4xl md:max-w-[800px]">
                  {el.title}
                </p>
                <p className="mt-3 hidden min-h-[32px] text-2xl en:text-xl md:block md:max-w-[800px]">
                  {el.description}
                </p>
                {el.button && (
                  <RouterLink href={el.button.href}>
                    <Button
                      variant="outline"
                      className="mt-4 rounded-3xl text-xs hover:!border-primary hover:!text-white md:min-w-[120px] lg:mt-8 lg:text-lg"
                      style={{
                        color: el.color,
                        borderColor: el.color,
                      }}>
                      {el.button.text}
                    </Button>
                  </RouterLink>
                )}
              </div>
              {!el.video && el.image && (
                <DirectusImage
                  fill
                  sizes="100vw"
                  id={el.image}
                  priority
                  className="object-cover"
                  alt={el.title || ""}
                />
              )}
              {/** 移动端只显示图片 */}
              {el.image && (
                <DirectusImage
                  fill
                  sizes="100vw"
                  id={el.image}
                  priority
                  className="object-cover md:hidden"
                  alt={el.title || ""}
                />
              )}
              {/** 配置了视频时，非移动端显示视频 */}
              {el.video && (
                <video
                  muted
                  autoPlay
                  src={`${process.env.NEXT_PUBLIC_ASSETS}/${el.video}`}
                  poster={
                    el.image
                      ? `${process.env.NEXT_PUBLIC_ASSETS}/${el.image}?transforms=[["blur",70]]`
                      : undefined
                  }
                  className="h-full w-full object-cover max-md:hidden"></video>
              )}
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className="absolute bottom-2.5 flex w-full items-center justify-center space-x-1.5 md:bottom-8">
          {scrollSnaps.map((_, index) => (
            <div
              key={index}
              style={{
                backgroundColor: props.data?.[current]?.color || "#2C2C2C",
              }}
              className={cn(
                "h-2 w-2 cursor-pointer rounded-full bg-[#2C2C2C] opacity-20  transition-all duration-300 ease-in-out",
                {
                  "w-5 opacity-80": current === index,
                }
              )}
              onClick={() => {
                mainApi?.scrollTo(index);
              }}></div>
          ))}
        </div>
      </CarouselContainer>
    </div>
  );
}
